import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartStore } from '../store';
import { formatPrice } from '../utils/helpers';
import SEO from '../components/common/SEO';
import { useState } from 'react';
import toast from 'react-hot-toast';

const CartPage = () => {
  const { items, removeItem, updateQuantity, clearCart, getTotal } = useCartStore();
  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const navigate = useNavigate();

  const subtotal = getTotal();
  const shipping = subtotal >= 5000 ? 0 : 250;
  const total = subtotal + shipping - discount;

  const applyPromo = () => {
    if (promoCode === 'SITSMART10') { setDiscount(Math.round(subtotal*0.1)); toast.success('10% discount applied!'); }
    else if (promoCode === 'NEWUSER') { setDiscount(500); toast.success('PKR 500 discount applied!'); }
    else toast.error('Invalid promo code');
  };

  if (items.length === 0) return (
    <>
      <SEO title="Your Cart"/>
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/></svg>
          </div>
          <h2 className="font-display text-3xl font-bold text-gray-900 mb-2">Your Cart is Empty</h2>
          <p className="text-gray-500 mb-6">Add some products to get started</p>
          <Link to="/shop" className="btn-primary inline-flex items-center gap-2 text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
            Continue Shopping
          </Link>
        </div>
      </div>
    </>
  );

  return (
    <>
      <SEO title="Shopping Cart"/>
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl font-black text-gray-900">
            Shopping Cart <span className="text-[#e63946]">({items.length})</span>
          </h1>
          <button onClick={clearCart} className="text-sm text-red-500 hover:underline flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
            Clear Cart
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <AnimatePresence>
              {items.map((item, idx) => (
                <motion.div key={`${item.id}_${item.selectedVariation}`}
                  initial={{ opacity:0, x:-20 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-20 }}
                  transition={{ delay:idx*0.05 }}
                  className="flex gap-5 p-5 bg-white border border-gray-100 hover:border-gray-200 transition-colors">
                  <Link to={`/product/${item.id}`} className="w-24 h-24 bg-gray-50 flex-shrink-0 overflow-hidden">
                    <img src={item.images?.[0]||'https://via.placeholder.com/96'} alt={item.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                      onError={e=>e.target.src='https://via.placeholder.com/96?text=Chair'}/>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.id}`}>
                      <h3 className="font-semibold text-gray-900 hover:text-[#1a3a6e] transition-colors">{item.title}</h3>
                    </Link>
                    {item.selectedVariation && <p className="text-xs text-gray-400 mt-0.5">{item.selectedVariation}</p>}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-gray-200">
                        <button onClick={()=>updateQuantity(item.id,item.selectedVariation,item.quantity-1)} className="px-3 py-1.5 hover:bg-gray-100 transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4"/></svg>
                        </button>
                        <span className="px-4 py-1.5 font-bold text-sm border-x border-gray-200">{item.quantity}</span>
                        <button onClick={()=>updateQuantity(item.id,item.selectedVariation,item.quantity+1)} className="px-3 py-1.5 hover:bg-gray-100 transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
                        </button>
                      </div>
                      <span className="font-black text-[#1a3a6e]">{formatPrice((item.salePrice||item.price)*item.quantity)}</span>
                    </div>
                  </div>
                  <button onClick={()=>removeItem(item.id,item.selectedVariation)} className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
            <Link to="/shop" className="flex items-center gap-2 text-[#1a3a6e] font-medium text-sm hover:gap-3 transition-all mt-4">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
              Continue Shopping
            </Link>
          </div>

          {/* Summary */}
          <div className="space-y-4">
            <div className="bg-white border border-gray-100 p-6">
              <h2 className="font-bold text-gray-900 text-lg mb-5">Order Summary</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-gray-600"><span>Subtotal ({items.length} items)</span><span>{formatPrice(subtotal)}</span></div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className={shipping===0?'text-green-600 font-medium':''}>{shipping===0?'FREE':formatPrice(shipping)}</span>
                </div>
                {discount>0&&<div className="flex justify-between text-green-600"><span>Discount</span><span>-{formatPrice(discount)}</span></div>}
                <div className="border-t border-gray-100 pt-3 flex justify-between font-black text-base">
                  <span>Total</span><span className="text-[#1a3a6e] text-xl">{formatPrice(total)}</span>
                </div>
              </div>
              {/* Promo */}
              <div className="mt-5">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/></svg>
                    <input type="text" placeholder="Promo code"
                      value={promoCode} onChange={e=>setPromoCode(e.target.value.toUpperCase())}
                      className="input-field pl-9 text-xs"/>
                  </div>
                  <button onClick={applyPromo} className="bg-[#1a3a6e] text-white px-4 text-sm font-bold hover:bg-[#e63946] transition-colors">Apply</button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Try: SITSMART10 or NEWUSER</p>
              </div>
              <button onClick={()=>navigate('/checkout')} className="btn-primary w-full text-center mt-5 text-sm">
                Proceed to Checkout
              </button>
            </div>
            <div className="text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
              Secure & Encrypted Checkout
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default CartPage;
